import React, { useState, useRef, useCallback, useEffect } from "react";
import CalculatorTemplate from "../../components/calculators/CalculatorTemplate";
import CalculatorPanel from "../../components/calculators/CalculatorPanel";
import CalculatorResults from "../../components/calculators/CalculatorResults";
import HeightInputGroup from "../../components/calculators/HeightInputGroup";
import CreatinineInputGroup from "../../components/calculators/CreatinineInputGroup";
import { useHeightInput, useSerumCreatinineInput } from "../../hooks/calculatorHooks";
import validateHeightField from "../../utils/calculators/validateHeightField";
import { GFR_HEIGHT_RANGES } from "../../utils/calculators/constants";
import { getHeightInCm } from "../../utils/lengthConverter";
import useErrorFocus from "../../hooks/useErrorFocus";
import gfr_formula from "./images/etg_abg15_appendix_figure1_children-renal-function.png";
import styles from "../../components/calculators/calculator.module.scss";
import { checkNum, sanityCheckGfr } from "../../utils/calculators/validation";
import { rounding } from "../../utils/calculators/general";
import { calculateGfr } from "../../utils/calculators/gfr";
import { isNumber } from "../../utils/isNumber";
import { forceDropdownProp } from "./forceDropdownProp";

const GfrCalcApp = (props) => {
  const { isWebLayout } = props;
  const [forceDropdown, setForceDropdown] = React.useState(false);

  useEffect(() => {
    setForceDropdown(forceDropdownProp(props));
  }, [props]);

  const {
    height,
    heightUnit,
    feet,
    inches,
    handleHeightChange,
    handleFeetChange,
    handleInchesChange,
    handleUnitChange,
    clearHeightFields,
    unitOptions: heightUnits,
  } = useHeightInput("cm");

  const {
    creatinineValue,
    creatinineUnit,
    handleCreatinineChange,
    handleCreatinineUnitChange,
    clearCreatinineFields,
    unitOptions: creatinineUnitOptions,
  } = useSerumCreatinineInput();

  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [showResult, setShowResult] = useState(false);
  const resultsRef = useRef(null);

  // Define field order for error focus management
  const fieldOrder = ["height", "serumCreatinine"];

  // Use the global error focus hook
  useErrorFocus(errors, fieldOrder);

  const wrappedHeightChange = useCallback(
    (e) => handleHeightChange(e, setErrors),
    [handleHeightChange],
  );
  const wrappedFeetChange = useCallback(
    (e) => handleFeetChange(e, setErrors),
    [handleFeetChange],
  );
  const wrappedInchesChange = useCallback(
    (e) => handleInchesChange(e, setErrors),
    [handleInchesChange],
  );
  const wrappedUnitChange = useCallback(
    (e) => handleUnitChange(e, setErrors),
    [handleUnitChange],
  );
  const wrappedCreatinineChange = useCallback(
    (e) => handleCreatinineChange(e, setErrors),
    [handleCreatinineChange],
  );
  const wrappedCreatinineUnitChange = useCallback(
    (e) => handleCreatinineUnitChange(e, setErrors),
    [handleCreatinineUnitChange],
  );

  function validateFields() {
    let fieldErrors = {};

    // unified height validation with GFR-specific ranges
    const heightError = validateHeightField(
      heightUnit,
      height,
      feet,
      inches,
      GFR_HEIGHT_RANGES,
    );
    if (heightError) fieldErrors.height = heightError;

    const cCheck = checkNum(creatinineValue, "serum creatinine");
    if (cCheck !== "") {
      fieldErrors.creatinine = cCheck;
    }

    // additional serum creatinine range validation for GFR
    if (!fieldErrors.creatinine) {
      const gfrSanity = sanityCheckGfr(
        0, // height not needed since we already validated it above
        creatinineValue,
        "cm",
        creatinineUnit,
      );
      if (gfrSanity !== true && typeof gfrSanity === "object") {
        const { creatinine: cErr, serum: sErr } = gfrSanity;
        if (cErr && cErr !== "") fieldErrors.creatinine = cErr;
        if (sErr && sErr !== true && sErr !== "") fieldErrors.creatinine = sErr;
      }
    }

    return fieldErrors;
  }

  function handleClear() {
    clearHeightFields();
    clearCreatinineFields();
    setResult(null);
    setErrors({});
    setShowResult(false);
  }

  function handleCalculate(e) {
    e.preventDefault();
    const fieldErrors = validateFields();
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      setShowResult(false);
      setResult(null);
      return;
    }
    const gfr = calculateGfr({
      heightCm: getHeightInCm(heightUnit, height, feet, inches),
      serumCreatinine: Number(creatinineValue),
      serumCreatinineUnit: creatinineUnit,
    });
    if (!isNumber(gfr)) {
      setResult(null);
      setErrors((prev) => ({
        ...prev,
        calculation: "Invalid input or calculation error.",
      }));
      setShowResult(false);
    } else {
      setResult(rounding(gfr, 0));
      setShowResult(true);
      setErrors({});
      setTimeout(() => {
        if (
          resultsRef.current &&
          typeof resultsRef.current.scrollIntoView === "function"
        ) {
          resultsRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 50);
    }
  }

  const resultUnit = (
    <span
      className={styles.resultUnit}
      aria-label="milliliters per minute per 1.73 square meters"
    >
      mL/min/1.73 m<sup>2</sup>
    </span>
  );

  const panel = (
    <CalculatorPanel header="Calculator" onClear={handleClear} errors={{}}>
      <HeightInputGroup
        idPrefix="gfr"
        height={height}
        heightUnit={heightUnit}
        feet={feet}
        inches={inches}
        onHeightChange={wrappedHeightChange}
        onFeetChange={wrappedFeetChange}
        onInchesChange={wrappedInchesChange}
        onUnitChange={wrappedUnitChange}
        error={errors.height}
        unitOptions={heightUnits}
        isWebLayout={isWebLayout}
        forceDropdown={forceDropdown}
      />
      <CreatinineInputGroup
        idPrefix="gfr"
        creatinineValue={creatinineValue}
        creatinineUnit={creatinineUnit}
        onCreatinineChange={wrappedCreatinineChange}
        onCreatinineUnitChange={wrappedCreatinineUnitChange}
        error={errors.creatinine}
        unitOptions={creatinineUnitOptions}
        isWebLayout={isWebLayout}
        forceDropdown={forceDropdown}
      />
      <div className={styles.calcBtnRow}>
        <button
          className={styles.calcBtn}
          type="submit"
          onClick={handleCalculate}
        >
          Calculate
        </button>
      </div>
    </CalculatorPanel>
  );

  const results = (
    <CalculatorResults
      label="GFR estimate"
      value={result}
      unit={result ? resultUnit : undefined}
      showResult={showResult}
    />
  );

  const formulaBlock = (
    <div className={isWebLayout ? "" : styles.formulaBlock}>
      <img
        src={gfr_formula}
        alt="GFR formula"
        className={isWebLayout ? "" : styles.formulaImage}
      />
    </div>
  );

  const description = (
    <div className={isWebLayout ? "" : styles.infoBlock}>
      <p>
        {isWebLayout
          ? "This calculator provides an estimate of glomerular filtration rate. It should only be used for children older than 1 year."
          : "The calculator below provides an estimate of glomerular filtration rate. It should only be used for children older than 1 year."}
      </p>
      <br />
      <p>The calculator is based on the modified Schwartz formula:</p>
    </div>
  );

  const calculatorTitle =
    "Glomerular filtration rate (GFR) calculator for children";

  return (
    <CalculatorTemplate
      title={calculatorTitle}
      description={description}
      formulaSection={formulaBlock}
      panel={panel}
      results={<div ref={resultsRef}>{results}</div>}
      isWebLayout={isWebLayout}
    />
  );
};

export default GfrCalcApp;
